import dayjs from 'dayjs';
import { ITask, isDateOnly } from '../TasksState';
import { MessageScenario, ITaskStatusContext } from '@innerhive-internal/innerhive-common';
import { TaskWrapUpStateContent } from './taskWrapUpTypes';

// Task wrap-up UI is shown from 7:05pm to midnight EST
const TASK_WRAP_UP_START_HOUR = 19; // 7:05pm in 24-hour format
const TASK_WRAP_UP_START_MINUTE = 5;

const pluralize = (count: number, singular: string, plural?: string): string => {
    return count === 1 ? singular : (plural || `${singular}s`);
};

/**
 * Check if current time (in EST) falls within task wrap-up UI window (7:05pm - midnight)
 * Can be overridden with dev setting: Debug Settings > Simulate Tasks Status toggle
 */
export const shouldShowTaskWrapUp = (): boolean => {
    // Dev override for testing via Debug Settings
    try {
        const devSettings = localStorage.getItem('dev-settings');
        if (devSettings) {
            const parsed = JSON.parse(devSettings);
            if (parsed.simulateTasksStatus === true) {
                return true;
            }
        }
    } catch {
        // Ignore localStorage errors
    }

    const estNow = dayjs().tz('America/New_York');
    const hour = estNow.hour();
    const minute = estNow.minute();

    // Task wrap-up UI is shown after 7:05pm EST until midnight
    if (hour < TASK_WRAP_UP_START_HOUR) {
        return false;
    }

    if (hour === TASK_WRAP_UP_START_HOUR && minute < TASK_WRAP_UP_START_MINUTE) {
        return false;
    }

    return true;
};

/**
 * Get display content for a message scenario
 * Maps the API's MessageScenario to UI display content
 */
export const getTaskWrapUpContent = (scenario: MessageScenario, context: ITaskStatusContext): TaskWrapUpStateContent => {
    const { taskCounts, hasTomorrowPlanned } = context;

    switch (scenario) {
        case 'high-achiever':
            if (hasTomorrowPlanned) {
                return {
                    title: '🌙 All Done for the Night!',
                    message: "You've finished everything for today and tomorrow is ready to go. Take a breath—you've earned this peace of mind.",
                    color: 'success',
                    actions: []
                };
            } else {
                return {
                    title: '🌙 All Done for the Day!',
                    message: "You've wrapped everything up for today. Time to relax and recharge!",
                    subMessage: 'Want to plan ahead?',
                    color: 'success',
                    actions: [
                        {
                            label: '+ Add Task for Tomorrow',
                            type: 'add-task',
                            action: 'add-task-tomorrow'
                        }
                    ]
                };
            }

        case 'made-progress':
            return {
                title: "💪 You're Making Progress!",
                message: `You finished ${taskCounts.completedToday} ${pluralize(taskCounts.completedToday, 'task')} today—nice work! You still have ${taskCounts.dueToday + taskCounts.overdue} ${pluralize(taskCounts.dueToday + taskCounts.overdue, 'task')} that could use a hand.`,
                color: 'tertiary',
                actions: [
                    {
                        label: 'Reschedule All to Tomorrow',
                        type: 'bulk',
                        action: 'reschedule-all'
                    },
                    {
                        label: 'Skip Remaining',
                        type: 'bulk',
                        action: 'skip-remaining'
                    }
                ]
            };

        case 'needs-catch-up':
            let needsCatchUpMessage = '';
            const totalTasks = taskCounts.overdue + taskCounts.dueToday;
            const pronoun = totalTasks === 1 ? 'it' : 'them';

            if (taskCounts.overdue > 0 && taskCounts.dueToday > 0) {
                needsCatchUpMessage = `You have ${taskCounts.overdue} ${pluralize(taskCounts.overdue, 'task')} from earlier and ${taskCounts.dueToday} ${pluralize(taskCounts.dueToday, 'task')} remaining today. Want to finish them now, skip, or move them to a better time?`;
            } else if (taskCounts.overdue > 0) {
                needsCatchUpMessage = `You have ${taskCounts.overdue} ${pluralize(taskCounts.overdue, 'task')} from earlier. Want to finish ${pronoun} now, skip, or move ${pronoun} to a better time?`;
            } else {
                needsCatchUpMessage = `You have ${taskCounts.dueToday} ${pluralize(taskCounts.dueToday, 'task')} remaining today. Want to finish ${pronoun} now, skip, or move ${pronoun} to a better time?`;
            }

            return {
                title: "👋 Let's Wrap Up Today",
                message: needsCatchUpMessage,
                color: 'warning',
                actions: [
                    {
                        label: 'Reschedule All to Tomorrow',
                        type: 'bulk',
                        action: 'reschedule-all'
                    },
                    {
                        label: 'Skip Remaining',
                        type: 'bulk',
                        action: 'skip-remaining'
                    }
                ]
            };

        case 'planning-mode':
            return {
                title: '🌙 All Set for Tomorrow!',
                message: "You're on top of things! Enjoy the evening knowing today's all set and tomorrow's planned out.",
                color: 'success',
                actions: []
            };

        case 'task-encouragement':
            return {
                title: '👣 Stay Ahead of the Curve',
                message: 'Your task list is clear! Create tasks below to keep up momentum and plan for success.',
                color: 'primary',
                actions: [
                    {
                        label: '+ Plan for Tomorrow',
                        type: 'add-task',
                        action: 'add-task-tomorrow'
                    }
                ]
            };

        case 'general-reminder':
        default:
            return {
                title: '📋 Daily Task Review',
                message: 'Your task list is clear! Use it to set yourself up for success:',
                color: 'primary',
                actions: [
                    {
                        label: '+ Plan for Tomorrow',
                        type: 'add-task',
                        action: 'add-task-tomorrow'
                    }
                ]
            };
    }
};

/**
 * Check if we should trigger celebration moment
 * Triggers when the last overdue or due-today task is completed/skipped
 */
export const shouldShowCelebration = (prevTasks: ITask[], currentTasks: ITask[]): boolean => {
    const today = dayjs().startOf('day');

    const formatTaskDateForComparison = (dueDate: string): dayjs.Dayjs => {
        if (isDateOnly(dueDate)) {
            return dayjs(dueDate + 'T00:00:00');
        } else {
            return dayjs(dueDate);
        }
    };

    // Count incomplete tasks due today or overdue
    const prevIncompleteCount = prevTasks.filter(t => {
        if (t.completed || t.skippedAt) return false;
        if (!t.dueDate) return false;

        const taskDate = formatTaskDateForComparison(t.dueDate).startOf('day');
        const daysUntilDue = taskDate.diff(today, 'days');

        return daysUntilDue <= 0;
    }).length;

    const currentIncompleteCount = currentTasks.filter(t => {
        if (t.completed || t.skippedAt) return false;
        if (!t.dueDate) return false;

        const taskDate = formatTaskDateForComparison(t.dueDate).startOf('day');
        const daysUntilDue = taskDate.diff(today, 'days');

        return daysUntilDue <= 0;
    }).length;

    // Celebration triggers when we go from >0 to 0 remaining urgent tasks
    return prevIncompleteCount > 0 && currentIncompleteCount === 0;
};
